import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { TrendingUp, AlertCircle, ClipboardCheck } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

const EvolutionPage = () => {
  const [evaluations, setEvaluations] = useState([]);
  const [nextDue, setNextDue] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [evalsRes, dueRes] = await Promise.all([
        axios.get(`${API}/evaluations/me`),
        axios.get(`${API}/evaluations/next-due`)
      ]);
      setEvaluations(evalsRes.data);
      setNextDue(dueRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  const latestEval = evaluations[0];

  const chartData = evaluations.slice().reverse().map(ev => ({
    date: new Date(ev.created_at).toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }),
    imc: ev.imc,
    gordura: ev.body_fat_percentage,
    massa: ev.lean_mass
  }));

  const getEvolutionColor = (classification) => {
    switch (classification) {
      case 'high': return 'text-emerald-600 bg-emerald-50';
      case 'moderate': return 'text-blue-600 bg-blue-50';
      case 'stable': return 'text-slate-600 bg-slate-100';
      case 'regression': return 'text-red-600 bg-red-50';
      default: return 'text-slate-600 bg-slate-100';
    }
  };

  const getEvolutionLabel = (classification) => {
    switch (classification) {
      case 'high': return 'Evolução Alta';
      case 'moderate': return 'Evolução Moderada';
      case 'stable': return 'Estável';
      case 'regression': return 'Regressão';
      default: return '-';
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Minha Evolução
        </h1>
        <p className="text-slate-600 mt-1">Acompanhe seu progresso físico</p>
      </div>

      {nextDue && (
        <Card className={`border-0 ${nextDue.due ? 'bg-orange-50' : 'bg-blue-50'}`}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              nextDue.due ? 'bg-orange-100' : 'bg-blue-100'
            }`}>
              {nextDue.due ? (
                <AlertCircle className={`w-5 h-5 ${nextDue.due ? 'text-orange-600' : 'text-blue-600'}`} />
              ) : (
                <ClipboardCheck className="w-5 h-5 text-blue-600" />
              )}
            </div>
            <div>
              <div className={`font-medium ${nextDue.due ? 'text-orange-900' : 'text-blue-900'}`}>
                {nextDue.due ? 'Avaliação Pendente!' : 'Próxima Avaliação'}
              </div>
              <div className={`text-sm ${nextDue.due ? 'text-orange-700' : 'text-blue-700'}`}>
                {nextDue.due
                  ? 'Procure sua academia para realizar a avaliação trimestral'
                  : `Faltam ${nextDue.days_remaining} dias para a próxima avaliação`
                }
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {evaluations.length === 0 ? (
        <Card className="border-slate-100">
          <CardContent className="p-12 text-center">
            <ClipboardCheck className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Nenhuma avaliação ainda</h3>
            <p className="text-slate-500">
              Quando sua academia realizar sua avaliação física, os dados aparecerão aqui.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {latestEval && (
            <Card className="border-slate-100">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Última Avaliação
                </CardTitle>
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                  getEvolutionColor(latestEval.evolution_classification)
                }`}>
                  <TrendingUp className="w-4 h-4" />
                  {getEvolutionLabel(latestEval.evolution_classification)}
                </span>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div>
                    <div className="text-slate-500 text-sm mb-1">IMC</div>
                    <div className="text-2xl font-bold text-slate-900">{latestEval.imc}</div>
                    <div className="text-xs text-slate-400">
                      {latestEval.imc < 25 ? 'Normal' : 'Acima'}
                    </div>
                  </div>
                  <div>
                    <div className="text-slate-500 text-sm mb-1">Gordura</div>
                    <div className="text-2xl font-bold text-slate-900">{latestEval.body_fat_percentage}%</div>
                    <div className="text-xs text-slate-400">Percentual</div>
                  </div>
                  <div>
                    <div className="text-slate-500 text-sm mb-1">Massa Magra</div>
                    <div className="text-2xl font-bold text-slate-900">{latestEval.lean_mass} kg</div>
                    <div className="text-xs text-slate-400">Total</div>
                  </div>
                  <div>
                    <div className="text-slate-500 text-sm mb-1">Peso</div>
                    <div className="text-2xl font-bold text-slate-900">{latestEval.weight} kg</div>
                    <div className="text-xs text-slate-400">{latestEval.height} cm</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {chartData.length > 1 && (
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="text-lg font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Evolução ao Longo do Tempo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#64748b' }} />
                      <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'white',
                          border: 'none',
                          borderRadius: '12px',
                          boxShadow: '0 8px 30px rgba(15,23,42,0.12)'
                        }}
                      />
                      <Line type="monotone" dataKey="massa" name="Massa Magra (kg)" stroke="#10B981" strokeWidth={2} dot={{ fill: '#10B981' }} />
                      <Line type="monotone" dataKey="gordura" name="Gordura (%)" stroke="#F97316" strokeWidth={2} dot={{ fill: '#F97316' }} />
                      <Line type="monotone" dataKey="imc" name="IMC" stroke="#2563EB" strokeWidth={2} dot={{ fill: '#2563EB' }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="border-slate-100">
            <CardHeader>
              <CardTitle className="text-lg font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Histórico de Avaliações
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-slate-100">
                {evaluations.map((evaluation) => (
                  <div key={evaluation.evaluation_id} className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="font-medium text-slate-900">
                        {new Date(evaluation.created_at).toLocaleDateString('pt-BR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </div>
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        getEvolutionColor(evaluation.evolution_classification)
                      }`}>
                        {getEvolutionLabel(evaluation.evolution_classification)}
                        {evaluation.evolution_percentage !== 0 && (
                          <span>({evaluation.evolution_percentage > 0 ? '+' : ''}{evaluation.evolution_percentage}%)</span>
                        )}
                      </span>
                    </div>
                    <div className="grid grid-cols-4 gap-4 text-sm">
                      <div>
                        <div className="text-slate-500">Peso</div>
                        <div className="font-medium">{evaluation.weight} kg</div>
                      </div>
                      <div>
                        <div className="text-slate-500">IMC</div>
                        <div className="font-medium">{evaluation.imc}</div>
                      </div>
                      <div>
                        <div className="text-slate-500">Gordura</div>
                        <div className="font-medium">{evaluation.body_fat_percentage}%</div>
                      </div>
                      <div>
                        <div className="text-slate-500">Massa</div>
                        <div className="font-medium">{evaluation.lean_mass} kg</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default EvolutionPage;
